import type { DesktopUpdateState } from '@/auth/desktopAuth';

export interface UpdatePresentation {
  title: string;
  detail: string;
  tone: 'neutral' | 'positive' | 'warning' | 'danger';
  primaryAction: 'check' | 'restart' | null;
}

export const shouldOfferDesktopRelaunch = (state: DesktopUpdateState | null): state is DesktopUpdateState => (
  Boolean(state?.configured && state.status === 'ready')
);

export const presentDesktopUpdate = (state: DesktopUpdateState): UpdatePresentation => {
  if (!state.configured) {
    return {
      title: 'Updates are unavailable in this technical build',
      detail: 'Install a signed EdgeLex release to receive managed application updates.',
      tone: 'warning',
      primaryAction: null,
    };
  }
  switch (state.status) {
    case 'checking':
      return { title: 'Checking for updates', detail: 'EdgeLex is checking the approved release channel.', tone: 'neutral', primaryAction: null };
    case 'downloading':
      return { title: 'Downloading the EdgeLex update', detail: 'You can keep working while the signed update is prepared.', tone: 'neutral', primaryAction: null };
    case 'ready':
      return { title: `EdgeLex ${state.availableVersion || 'update'} is ready`, detail: 'Restart EdgeLex to install the downloaded update.', tone: 'positive', primaryAction: 'restart' };
    case 'installing':
      return { title: 'Restarting EdgeLex', detail: 'The update will be installed before EdgeLex opens again.', tone: 'positive', primaryAction: null };
    case 'up-to-date':
      return { title: 'EdgeLex is up to date', detail: `Version ${state.currentVersion} is the newest release on your channel.`, tone: 'positive', primaryAction: 'check' };
    case 'failed':
      return { title: 'EdgeLex could not check for updates', detail: 'Your current application remains unchanged. Try again when the connection is available.', tone: 'danger', primaryAction: 'check' };
    default:
      return { title: 'Keep EdgeLex current', detail: 'Check the approved release channel for application and security updates.', tone: 'neutral', primaryAction: 'check' };
  }
};
